import { Helmet } from 'react-helmet-async';
import { FAQ_ACCUEIL } from '../data/faq';
import { ENTREPRISE, POSTAL_ADDRESS, GEO_COORDINATES } from '../data/entreprise';
import { publiableAuxAgents, valeurDe, type CleFait } from '../data/registre';
import servicesJson from '../data/services.json';

/**
 * Catalogue des prestations — LA MÊME source que l'outil WebMCP `get_service`.
 *
 * C'est le principe du repli structuré : une seule donnée, deux sorties. Un
 * navigateur qui expose WebMCP appelle l'outil ; tous les autres — c'est-à-dire
 * l'écrasante majorité, et pour longtemps — lisent le JSON-LD. Les deux disent
 * la même chose parce qu'ils lisent le même fichier.
 *
 * Le repli n'est pas transitoire. L'essai d'origine expire le 17/11/2026, son
 * renouvellement n'est pas garanti, Firefox et Safari ne se sont engagés à
 * rien, et Google précise que la fonctionnalité peut ne jamais dépasser
 * l'expérimentation. Le JSON-LD, lui, est lu par tout le monde aujourd'hui.
 */
interface PrestationCatalogue {
  id: string;
  nom: string;
  page: string;
  resume: string;
  inclus: string[];
  options: string[];
}
const CATALOGUE = (servicesJson as unknown as { services: PrestationCatalogue[] }).services;

const BASE_URL = 'https://www.demenagements-gramme.be';

/**
 * Valeur d'un fait, à la condition qu'il ait le droit d'aller aux machines.
 *
 * Le JSON-LD est LA surface machine : ce qu'on y écrit est repris tel quel par
 * des moteurs et des assistants qui ne remonteront jamais à la page. Un fait en
 * `'a-confirmer'` — la satisfaction de 95%, l'étendue de l'assurance — n'y a
 * donc pas sa place tant que Gramme n'a pas répondu.
 *
 * La règle est appliquée ICI plutôt que rappelée en commentaire : passer par
 * `valeurDe` directement pour un fait non vérifié interrompt le pré-rendu, donc
 * le build, donc le déploiement. Une règle qu'on peut oublier n'est pas une
 * règle.
 */
function pourMachines<K extends CleFait>(cle: K): ReturnType<typeof valeurDe<K>> {
  if (!publiableAuxAgents(cle)) {
    throw new Error(
      `JSON-LD : le fait « ${cle} » est en 'a-confirmer' et ne peut pas être structuré. ` +
      'Voir src/data/registre.ts et docs/QUESTIONS_OUVERTES.md.',
    );
  }
  return valeurDe(cle);
}

/** Premier pays du registre — la Belgique. Utilisé partout où un seul est déclaré. */
const PAYS_PRINCIPAL = pourMachines('couverture.pays')[0];

/**
 * F1 du plan correctif : la grille du garde-meubles, en `Offer`.
 *
 * C'est la donnée la plus structurable du site — huit paliers publics, fermes,
 * et qu'aucun concurrent ne publie — et elle n'existait qu'en `<table>`. Un
 * moteur de réponse qui cherche « prix garde-meubles Liège » devait la lire
 * dans du HTML de présentation, ou renoncer.
 *
 * Elle est dérivée du registre, comme le tableau HTML lui-même : la page et le
 * balisage ne peuvent plus diverger, puisqu'ils lisent la même chose.
 *
 * `UnitPriceSpecification` plutôt que `PriceSpecification` : le prix est
 * mensuel, et `billingDuration` / `unitCode` sont ce qui le dit — sans quoi
 * « 30 € » se lit comme un prix d'achat.
 */
function offresGardeMeubles() {
  return pourMachines('tarifs.garde_meubles').map((palier) => ({
    '@type': 'Offer',
    name:
      palier.unite === 'm3-mois'
        ? `Garde-meubles au-delà de 30 m³`
        : `Garde-meubles ${palier.libelle}`,
    itemOffered: {
      '@type': 'Service',
      name: `Box de stockage ${palier.libelle}`,
      serviceType: 'Garde-meubles',
      provider: { '@id': ORG_ID },
    },
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      price: palier.prixEuros,
      priceCurrency: 'EUR',
      // « par mois », ou « par m³ et par mois » pour le dernier palier.
      unitText: palier.unite === 'm3-mois' ? 'par m³ et par mois' : 'par mois',
      billingIncrement: 1,
      billingDuration: 1,
      unitCode: 'MON',
      ...(palier.volumeM3 !== null
        ? { referenceQuantity: { '@type': 'QuantitativeValue', value: palier.volumeM3, unitCode: 'MTQ' } }
        : {}),
    },
    availability: 'https://schema.org/InStock',
    eligibleDuration: {
      '@type': 'QuantitativeValue',
      minValue: 1,
      unitCode: 'MON',
      description: pourMachines('tarifs.garde_meubles.duree_minimum'),
    },
  }));
}

/**
 * Identifiant stable de l'entreprise, partagé par toutes les pages.
 *
 * C'est lui qui permet de ne décrire l'établissement qu'une fois et de s'y
 * référer ailleurs, au lieu de redéclarer la même adresse, les mêmes horaires
 * et la même géolocalisation sur chaque page.
 */
export const ORG_ID = `${BASE_URL}/#organization`;

const LOCAL_BUSINESS = {
  '@context': 'https://schema.org',
  '@type': 'MovingCompany',
  '@id': ORG_ID,
  name: 'Déménagements Gramme',
  image: `${BASE_URL}/og-image.jpg`,
  url: BASE_URL,
  telephone: `+${ENTREPRISE.telephone.lien.replace('+', '')}`,
  email: ENTREPRISE.email,
  // `sameAs` rattache l'entité du site à ses profils officiels, ce qui aide
  // Google à ne pas la confondre avec une homonyme.
  //
  // F3 du plan correctif : il ne contenait que Facebook, alors qu'un lien vers
  // la fiche Google Maps existait dans le corps de la page des avis. C'est le
  // signal de résolution d'entité le plus fort qui soit — la fiche
  // d'établissement EST l'entité, du point de vue de Google — et il n'était pas
  // déclaré.
  sameAs: [
    'https://www.facebook.com/GrammeDemenagements',
    pourMachines('avis.google').url,
  ],
  logo: `${BASE_URL}/logo-gramme-300.png`,
  address: POSTAL_ADDRESS,
  geo: GEO_COORDINATES,
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: 'Monday',
      opens: ENTREPRISE.horaires.lundi.ouverture,
      closes: ENTREPRISE.horaires.lundi.fermeture,
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: ENTREPRISE.horaires.mardiAuVendredi.ouverture,
      closes: ENTREPRISE.horaires.mardiAuVendredi.fermeture,
    },
    // C7 du plan correctif : le dimanche était déclaré `opens: "00:00",
    // closes: "00:00"`. L'intention — dire « fermé » plutôt que de laisser le
    // jour indéterminé — était bonne, l'écriture ne l'était pas : cette paire
    // est ambiguë, et plusieurs parseurs la lisent comme une ouverture de
    // minuit à minuit, c'est-à-dire vingt-quatre heures sur vingt-quatre. Le
    // jour le PLUS sûrement fermé de la semaine devenait le seul déclaré
    // toujours ouvert.
    //
    // La convention Schema.org pour un jour de fermeture est de ne pas le
    // déclarer. L'information « fermé le dimanche » reste en texte visible sur
    // la page, dans llms.txt et dans `get_company_info` — trois surfaces qui la
    // disent en toutes lettres, sans ambiguïté d'interprétation.
  ],
  foundingDate: '1948',
  vatID: 'BE0775264382',
  // C4 du plan correctif. Cinq pays étaient déclarés ici, neuf dans la FAQ,
  // « cinq plus le reste de l'Europe » dans llms.txt : trois listes pour une
  // seule réalité. Elles viennent maintenant du registre, qui retient celle de
  // la fiche « déménagement international » — la seule des trois qui décrive le
  // service au lieu d'en résumer une partie.
  areaServed: pourMachines('couverture.pays').map((pays) => ({
    '@type': 'Country',
    name: pays,
  })),

  // F2 du plan correctif : `priceRange` valait « Sur devis », qui n'est pas
  // parseable — la propriété attend une fourchette ou une notation de niveau de
  // prix. Une valeur en texte libre n'informe personne et occupe une place
  // qu'un validateur signale.
  //
  // Elle est donc retirée plutôt que remplie au jugé. Le seul prix ferme du
  // site est celui du garde-meubles, et il est déclaré là où il est vrai :
  // dans l'`Offer` de la prestation correspondante, ci-dessous — pas comme
  // fourchette de prix de l'entreprise entière, ce qu'il n'est pas.

  // F4 du plan correctif : `aggregateRating` n'est délibérément PAS émis.
  //
  // La note de 4,0/5 sur 24 avis est réelle et affichée sur la page. Mais le
  // balisage d'avis auto-déclarés sur sa propre organisation est encadré par
  // Google, qui l'ignore ou le sanctionne selon les cas : la règle veut que la
  // note provienne d'un tiers indépendant, et une note Google republiée par
  // l'établissement lui-même n'en est pas une. Le gain est nul, le risque ne
  // l'est pas.
  //
  // Le rattachement à la fiche d'établissement se fait par `sameAs` ci-dessus,
  // qui est la façon correcte de dire « cette note est là-bas ».
  // Le catalogue, dérivé du même fichier que l'outil `get_service`. Il dit à un
  // moteur ce que l'entreprise propose sans qu'il ait à le déduire des titres
  // de pages.
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Prestations de Déménagements Gramme',
    itemListElement: CATALOGUE.map((s) => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: s.nom,
        url: `${BASE_URL}${s.page}`,
      },
    })),
  },
};

/**
 * Référence compacte à l'entreprise, servie sur toutes les pages sauf celles
 * qui décrivent réellement l'établissement.
 *
 * Elle porte le même `@id` que la déclaration complète : les moteurs rattachent
 * donc la page à l'entité déjà connue, sans qu'une soixantaine de pages répètent
 * la géolocalisation, les horaires, la liste de pays desservis et le numéro de
 * TVA. C'est cette répétition-là qui faisait ressembler chaque page commune à
 * une déclaration d'établissement distincte.
 *
 * ⚠️ `name` et `address` ne sont PAS optionnels ici, malgré le mot « référence ».
 *
 * MovingCompany est une sous-classe de LocalBusiness, et Google exige les deux
 * champs sur tout nœud de ce type. Une première version omettait l'adresse — le
 * nœud se voulait un simple renvoi par `@id` — et les validateurs ont lu une
 * déclaration LocalBusiness incomplète sur 89 pages. Le type détermine ce qui
 * est exigé, pas l'intention.
 *
 * Retirer l'adresse d'ici casse donc à nouveau la validation. Pour alléger le
 * nœud, retirer d'autres propriétés, jamais celles-là.
 */
const ORG_REFERENCE = {
  '@context': 'https://schema.org',
  '@type': 'MovingCompany',
  '@id': ORG_ID,
  name: 'Déménagements Gramme',
  url: BASE_URL,
  telephone: '+3242645016',
  address: LOCAL_BUSINESS.address,
};

// Généré depuis la source partagée des questions, et non écrit en dur : Google
// exige que le balisage FAQPage corresponde au contenu réellement affiché. La
// version précédente listait 4 questions alors que la page en montre 9.
const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ACCUEIL.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: {
      '@type': 'Answer',
      // La liste à puces fait partie de la réponse visible : on la reprend.
      text: [f.a, ...(f.list ?? [])].filter(Boolean).join(' '),
    },
  })),
};

export interface ArticleData {
  title: string;
  publishDate: string;
  url: string;
  image?: string;
}

export interface BreadcrumbItem {
  name: string;
  url: string;
}

/** Question réellement affichée sur la page courante. */
export interface FaqItem {
  q: string;
  a: string;
}

/**
 * Provenance d'une règle locale, telle qu'elle est déjà relevée commune par
 * commune dans `communes.json` (`AutorisationStationnement`).
 *
 * F5 du plan correctif. Le site relève, pour cinquante et une communes,
 * l'autorité compétente en matière d'autorisation de stationnement, la
 * procédure, le délai, la redevance, la source officielle et la DATE du relevé
 * — et un humain a relu chacune. C'est un travail que personne d'autre ne fait
 * sur ce marché, et il n'était structuré nulle part : il vivait en paragraphes
 * dans le corps de la page.
 *
 * Le structurer, c'est le rendre citable. Un assistant à qui l'on demande « il
 * faut combien de temps pour réserver un emplacement à Herstal ? » peut alors
 * répondre AVEC sa source et sa date, au lieu de deviner.
 */
export interface ProvenanceLocale {
  autorite: string;
  procedure: string;
  delai: string | null;
  cout: string | null;
  sourceUrl: string;
  libelleSource: string;
  dateVerification: string;
}

/** Ville desservie, pour le balisage Service des pages locales. */
export interface LocalServiceData {
  ville: string;
  codesPostaux: string[];
  url: string;
  /**
   * Absente tant que la fiche n'a pas été relue par un humain
   * (`verifiePar === 'human'`). Une provenance non relue ne vaut pas mieux
   * qu'aucune provenance : elle donne à une donnée incertaine l'apparence
   * d'une donnée sourcée.
   */
  provenance?: ProvenanceLocale;
}

interface SchemaOrgProps {
  /**
   * `'full'` déclare l'établissement (adresse, horaires, géolocalisation) :
   * réservé aux pages qui parlent réellement du siège, c'est-à-dire l'accueil
   * et la page Seraing. Partout ailleurs, la valeur par défaut `'reference'`
   * se contente de rattacher la page à l'entité par son `@id`.
   */
  organization?: 'full' | 'reference';
  includeFaq?: boolean;
  /**
   * FAQ propre à la page, quand ce ne sont pas les questions de l'accueil.
   *
   * Indispensable pour les pages locales : `includeFaq` injecterait les 9
   * questions de l'accueil sur une page qui n'en affiche aucune, ce que Google
   * traite comme du balisage non conforme au contenu visible.
   */
  customFaq?: FaqItem[];
  articleData?: ArticleData;
  breadcrumbs?: BreadcrumbItem[];
  localService?: LocalServiceData;
  /**
   * Chemin canonique de la page, quand elle décrit une prestation du catalogue.
   *
   * La correspondance se fait sur `page` dans src/data/services.json : aucune
   * table de correspondance à tenir à la main, et une prestation retirée du
   * catalogue cesse d'être balisée sans qu'il faille y penser. Une page qui ne
   * correspond à rien n'émet simplement pas de nœud `Service`.
   */
  pagePrestation?: string;
  /**
   * Liste ordonnée d'éléments réellement affichés sur la page — les communes
   * desservies, sur la page des zones d'intervention. Un `url` n'est fourni
   * que pour les entrées qui pointent vers une page existante : référencer une
   * URL absente reviendrait à décrire une page qui n'existe pas.
   */
  itemList?: {
    name: string;
    items: Array<{ name: string; url?: string }>;
  };
  /**
   * Page pays du déménagement international : un `Service` dont l'`areaServed`
   * est la Belgique ET le pays de destination, rattaché à l'organisation par
   * son `@id`. Distinct de `pagePrestation`, qui déclare la Belgique seule, et
   * de `localService`, qui déclare une commune.
   */
  serviceVers?: {
    pays: string;
    nom: string;
    url: string;
  };
  /**
   * Procédure séquentielle réellement affichée sur la page — la checklist du
   * changement d'adresse (lot 11). Réservé à une page dont le corps EST une
   * suite d'étapes ordonnées : une fiche opérateur, qui décrit une démarche
   * mais n'est pas une procédure pas à pas, porte une FAQPage, pas un HowTo.
   */
  howTo?: {
    name: string;
    description: string;
    url: string;
    steps: Array<{ name: string; text: string }>;
  };
  /**
   * Une prestation du site desservant une commune — le garde-meubles pour
   * Verviers (lot 7). Même principe que `localService` : un `Service` avec
   * `areaServed`, jamais un second établissement. Le garde-meubles est à
   * Seraing ; déclarer un LocalBusiness à Verviers serait une fausse
   * implantation. Pas d'`offers` non plus : les prix fermes du site ne sont
   * déclarés que sur la page du garde-meubles.
   */
  serviceZone?: {
    serviceType: string;
    name: string;
    description: string;
    url: string;
    ville: string;
    codesPostaux: string[];
  };
}

/**
 * Sérialise un objet en JSON-LD prêt à être posé dans un `<script>`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * POURQUOI CE N'EST PAS UN `JSON.stringify` NU
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Un `<script>` est analysé en TEXTE BRUT : le navigateur y cherche la première
 * séquence `</script` et ferme la balise là, sans se soucier du JSON. Or
 * `JSON.stringify` n'échappe pas le `<` — il n'a aucune raison de le faire,
 * puisqu'il produit du JSON, pas du HTML.
 *
 * Conséquence : une seule chaîne contenant `</script>` quelque part dans les
 * données coupe la balise en deux, et TOUT ce qui suit — le reste du JSON, puis
 * le vrai HTML de la page — est réinterprété par le parseur. Le balisage
 * structuré devient invalide, la page se déforme, et sur une donnée bien
 * choisie cela devient une injection.
 *
 * Ce n'est pas une hypothèse d'école ici : ce JSON-LD est composé de textes de
 * communes, de FAQ et de faits du registre, tous éditables depuis Supabase.
 * Personne n'écrirait `</script>` volontairement — mais personne ne l'interdit
 * non plus, et rien ne le signalerait avant que la page ne casse.
 *
 * `\u003c` est la forme échappée de `<` en JSON. Tout analyseur la redécode en
 * `<` : le balisage reste strictement équivalent pour Google et pour un agent,
 * et la séquence `</script` ne peut plus apparaître dans la sortie.
 */
function serialiserLd(valeur: unknown): string {
  return JSON.stringify(valeur).replace(/</g, '\\u003c');
}

export default function SchemaOrg({
  organization = 'reference',
  includeFaq = false,
  customFaq,
  articleData,
  breadcrumbs,
  localService,
  pagePrestation,
  itemList,
  serviceVers,
  howTo,
  serviceZone,
}: SchemaOrgProps) {
  const scripts: Array<{ type: string; innerHTML: string }> = [
    {
      type: 'application/ld+json',
      innerHTML: serialiserLd(organization === 'full' ? LOCAL_BUSINESS : ORG_REFERENCE),
    },
  ];

  if (includeFaq) {
    scripts.push({ type: 'application/ld+json', innerHTML: serialiserLd(FAQ_SCHEMA) });
  }

  if (customFaq && customFaq.length > 0) {
    scripts.push({
      type: 'application/ld+json',
      innerHTML: serialiserLd({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: customFaq.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      }),
    });
  }

  if (pagePrestation) {
    const prestation = CATALOGUE.find((s) => s.page === pagePrestation);
    if (prestation) {
      scripts.push({
        type: 'application/ld+json',
        innerHTML: serialiserLd({
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: prestation.nom,
          name: prestation.nom,
          description: prestation.resume,
          url: `${BASE_URL}${prestation.page}`,
          // Renvoi par `@id` vers l'entité unique. Redéclarer l'établissement
          // sur chaque page de service produirait autant de LocalBusiness
          // incomplets — l'erreur exacte que le contrôle de build attrape
          // depuis qu'elle est arrivée sur 89 pages.
          provider: { '@id': ORG_ID },
          areaServed: { '@type': 'Country', name: PAYS_PRINCIPAL },
          // La page du garde-meubles porte les SEULS prix fermes du site. Ils y
          // sont déclarés, et nulle part ailleurs : un prix vrai sur une page
          // qui le publie, plutôt qu'un `priceRange` approximatif sur
          // l'entreprise entière.
          ...(prestation.id === 'garde-meubles'
            ? {
                offers: offresGardeMeubles(),
              }
            : {}),
          ...(prestation.inclus.length
            ? {
                hasOfferCatalog: {
                  '@type': 'OfferCatalog',
                  name: `Compris dans ${prestation.nom}`,
                  itemListElement: [...prestation.inclus, ...prestation.options].map((libelle) => ({
                    '@type': 'Offer',
                    itemOffered: { '@type': 'Service', name: libelle.slice(0, 120) },
                  })),
                },
              }
            : {}),
        }),
      });
    }
  }

  if (localService) {
    // Service plutôt qu'une seconde MovingCompany : l'entreprise est unique et
    // située à Seraing. Déclarer un établissement par commune serait une
    // fausse déclaration d'implantation locale.
    scripts.push({
      type: 'application/ld+json',
      innerHTML: serialiserLd({
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: 'Déménagement',
        name: `Déménagement à ${localService.ville}`,
        url: localService.url,
        // Renvoi par `@id` : le prestataire est l'entité unique du site. La
        // version précédente recopiait ici l'adresse du siège, ce qui
        // revenait à répéter le même bloc d'établissement sur chaque commune.
        provider: { '@id': ORG_ID },
        areaServed: {
          '@type': 'City',
          name: localService.ville,
          // `postalCode` appartient à PostalAddress, pas à Place : posé
          // directement sur la City, il était rejeté à la validation. Il
          // rejoint donc l'adresse, qui est le bon porteur.
          address: {
            '@type': 'PostalAddress',
            addressLocality: localService.ville,
            ...(localService.codesPostaux.length > 0 && {
              postalCode: localService.codesPostaux.join(', '),
            }),
            addressRegion: 'Province de Liège',
            addressCountry: 'BE',
          },
        },
        // F5 : la provenance des règles locales, structurée.
        //
        // `additionalProperty` porte les faits, `citation` porte leur source et
        // sa date. Aucun des deux n'est décoratif : c'est ce qui permet à un
        // assistant de répondre « d'après {autorité}, relevé le {date} » plutôt
        // que d'affirmer un délai sans savoir d'où il vient — et c'est
        // exactement l'écart entre une réponse citable et une réponse qu'un
        // client contredira à la commune.
        ...(localService.provenance
          ? {
              additionalProperty: [
                {
                  '@type': 'PropertyValue',
                  name: 'Autorisation de stationnement — autorité compétente',
                  value: localService.provenance.autorite,
                },
                {
                  '@type': 'PropertyValue',
                  name: 'Autorisation de stationnement — procédure',
                  value: localService.provenance.procedure,
                },
                // Délai et redevance ne sont déclarés que si la commune les
                // PUBLIE. Les cinquante et une fiches relues portent `null`
                // partout où l'information n'existe pas, et cette absence est
                // reprise telle quelle : un délai plausible inventé ici ferait
                // prendre une amende à quelqu'un.
                ...(localService.provenance.delai
                  ? [{
                      '@type': 'PropertyValue',
                      name: 'Autorisation de stationnement — délai minimum',
                      value: localService.provenance.delai,
                    }]
                  : []),
                ...(localService.provenance.cout
                  ? [{
                      '@type': 'PropertyValue',
                      name: 'Autorisation de stationnement — redevance',
                      value: localService.provenance.cout,
                    }]
                  : []),
              ],
              citation: {
                '@type': 'CreativeWork',
                name: localService.provenance.libelleSource,
                url: localService.provenance.sourceUrl,
                // La date du RELEVÉ, pas celle de la page source : c'est elle
                // qui dit depuis quand l'information peut avoir bougé.
                dateModified: localService.provenance.dateVerification,
              },
            }
          : {}),
      }),
    });
  }

  if (serviceVers) {
    scripts.push({
      type: 'application/ld+json',
      innerHTML: serialiserLd({
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: 'Déménagement international',
        name: serviceVers.nom,
        url: serviceVers.url,
        provider: { '@id': ORG_ID },
        // Le pays de départ et celui d'arrivée : c'est ce qui distingue une
        // page pays du hub, dont le Service déclare la Belgique seule.
        areaServed: [
          { '@type': 'Country', name: PAYS_PRINCIPAL },
          { '@type': 'Country', name: serviceVers.pays },
        ],
      }),
    });
  }

  if (articleData) {
    scripts.push({
      type: 'application/ld+json',
      innerHTML: serialiserLd({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: articleData.title,
        image: articleData.image || `${BASE_URL}/og-image.jpg`,
        author: {
          '@type': 'Organization',
          name: 'Déménagements Gramme',
          url: BASE_URL,
        },
        publisher: {
          '@type': 'Organization',
          name: 'Déménagements Gramme',
          logo: { '@type': 'ImageObject', url: `${BASE_URL}/logo-gramme-300.png` },
        },
        datePublished: articleData.publishDate,
        dateModified: articleData.publishDate,
        url: articleData.url,
        mainEntityOfPage: { '@type': 'WebPage', '@id': articleData.url },
      }),
    });
  }

  if (serviceZone) {
    scripts.push({
      type: 'application/ld+json',
      innerHTML: serialiserLd({
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: serviceZone.serviceType,
        name: serviceZone.name,
        description: serviceZone.description,
        url: serviceZone.url,
        provider: { '@id': ORG_ID },
        areaServed: {
          '@type': 'City',
          name: serviceZone.ville,
          address: {
            '@type': 'PostalAddress',
            addressLocality: serviceZone.ville,
            ...(serviceZone.codesPostaux.length > 0 && { postalCode: serviceZone.codesPostaux.join(', ') }),
            addressRegion: 'Province de Liège',
            addressCountry: 'BE',
          },
        },
      }),
    });
  }

  if (howTo && howTo.steps.length > 0) {
    scripts.push({
      type: 'application/ld+json',
      innerHTML: serialiserLd({
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: howTo.name,
        description: howTo.description,
        url: howTo.url,
        step: howTo.steps.map((s, i) => ({
          '@type': 'HowToStep',
          position: i + 1,
          name: s.name,
          text: s.text,
          url: `${howTo.url}#etape-${i + 1}`,
        })),
      }),
    });
  }

  if (itemList && itemList.items.length > 0) {
    scripts.push({
      type: 'application/ld+json',
      innerHTML: serialiserLd({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: itemList.name,
        numberOfItems: itemList.items.length,
        itemListElement: itemList.items.map((item, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: item.name,
          ...(item.url && { url: item.url }),
        })),
      }),
    });
  }

  if (breadcrumbs && breadcrumbs.length > 0) {
    scripts.push({
      type: 'application/ld+json',
      innerHTML: serialiserLd({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: crumb.name,
          item: crumb.url,
        })),
      }),
    });
  }

  // react-helmet-async fusionne proprement les <script> JSON-LD dans le <head>,
  // aussi bien côté client (hydratation) que côté serveur (pré-rendu).
  return <Helmet script={scripts} />;
}
